import React, { useRef, useEffect, useState } from 'react';
import styles from './AbilitiesTimeline.module.css';
import { DetailedRoundData } from '../utils/types';
import { ABILITIES } from '../utils/abilities';


interface AbilitiesTimelineProps {
  selectedPlayerIndices: number[];
  roundData: DetailedRoundData | undefined;
  roundDuration: number;
  currentRoundTime: number;
  onTimeChange?: (time: number) => void;
}

const getAbilityIcon = (agent: string, abilityIndex: number, isUltimate: boolean): { image_url: string, name: string } => {
  const agentAbilities = ABILITIES[agent];
  if (!agentAbilities) return { image_url: '', name: '' };

  if (isUltimate) {
    return agentAbilities[0];
  }
  return agentAbilities[abilityIndex];
};

const AbilitiesTimeline: React.FC<AbilitiesTimelineProps> = ({ 
  selectedPlayerIndices, roundData, roundDuration, currentRoundTime, onTimeChange 
}) => {
  const timelineRef = useRef<HTMLDivElement>(null);
  const [timelineWidth, setTimelineWidth] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const handleAbilityClick = (timeSeconds: number) => {
    if (onTimeChange) {
      onTimeChange(timeSeconds - 1.25);
    }
  };

  useEffect(() => {
    // Set loading state when roundData changes
    if (!roundData) {
      setIsLoading(true);
    } else {
      // Simulate a small delay to prevent flash of loading state
      const timer = setTimeout(() => {
        setIsLoading(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [roundData]);

  useEffect(() => {
    const updateWidth = () => {
      if (timelineRef.current) {
        setTimelineWidth(timelineRef.current.offsetWidth);
      }
    };

    // Initial measurement
    updateWidth();

    // Add resize listener
    window.addEventListener('resize', updateWidth);

    // Cleanup
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const abilityUses = React.useMemo(() => {
    if (!roundData || !roundData.abilityUsage) return [];
    // Combine ability uses from all selected players
    return selectedPlayerIndices.flatMap(playerIndex => {
      const playerAbilities = roundData?.abilityUsage?.[playerIndex] || [];
      return playerAbilities.map(ability => ({
        ...ability,
        playerIndex // Add playerIndex to track which player used the ability
      }));
    }).sort((a, b) => a.timeSeconds - b.timeSeconds);
  }, [roundData, selectedPlayerIndices]);

  const PADDING_LEFT = 6; // pixels
  const PADDING_RIGHT = 6; // pixels

  const renderTimelineMarkers = () => {
    const markers = [];
    for (let i = 0; i <= roundDuration; i++) {
      const leftPosition = ((i / roundDuration) * (timelineWidth - PADDING_LEFT - PADDING_RIGHT) + PADDING_LEFT) / timelineWidth * 100;
      
      const isRoundStart = i === 40; // 1:40 mark
      const isTenSecondMark = i % 10 === 0;
      
      markers.push(
        <div
          key={`marker-${i}`}
          className={`${styles.timelineMarker} ${isRoundStart ? styles.timelineMarkerRoundStart : ''} ${isTenSecondMark ? styles.timelineMarkerTenSeconds : ''}`}
          style={{
            left: `${leftPosition}%`,
          }}
        />
      );
    }
    return markers;
  };

  // Clamp the currentRoundTime to ensure it doesn't exceed roundDuration
  const clampedTime = Math.min(currentRoundTime, roundDuration);

  if (isLoading) {
    return (
      <div className={styles.abilitiesTimeline} ref={timelineRef}>
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  if (abilityUses.length === 0 && roundData) {
    return (
      <div className={styles.abilitiesTimeline} ref={timelineRef}>
        <div className={styles.emptyContainer}>
          Abilities not used or available
        </div>
      </div>
    );
  }

  return (
    <div className={styles.abilitiesTimeline} ref={timelineRef}>
      {timelineWidth > 0 && renderTimelineMarkers()}
      <div 
        className={styles.progressBar}
        style={{
          paddingLeft: `${PADDING_LEFT}px`,
          width: `calc(${(clampedTime / roundDuration) * (timelineWidth - PADDING_LEFT - PADDING_RIGHT)}px + ${PADDING_LEFT}px)`
        }}
      />
      {abilityUses.map((use, index) => {
        const leftPosition = ((use.timeSeconds / roundDuration) * (timelineWidth - PADDING_LEFT - PADDING_RIGHT) + PADDING_LEFT) / timelineWidth * 100;
        const agent = roundData?.frames[0].players[use.playerIndex].agent || '';
        
        const abilityIcon = getAbilityIcon(
          agent.toLowerCase(),
          use.type === 'ultimate' ? 0 : parseInt(use.type.slice(-1)),
          use.type === 'ultimate'
        );
        
        return (
          <div
            key={index}
            className={`${styles.abilityIcon} ${use.timeSeconds <= currentRoundTime ? styles.abilityIconUsed : styles.abilityIconUnused}`}
            style={{
              left: `${leftPosition}%`,
            }}
          >
            <div 
              className={`${styles.abilityIconWrapper} ${use.timeSeconds <= currentRoundTime ? styles.abilityIconWrapperUsed : styles.abilityIconWrapperUnused}`}
              onClick={() => handleAbilityClick(use.timeSeconds)}
              style={{ cursor: 'pointer', zIndex: 1000, pointerEvents: 'all' }}
            >
              <img 
                src={abilityIcon.image_url} 
                alt={abilityIcon.name}
                title={`${agent} - ${abilityIcon.name}`}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default AbilitiesTimeline;